import { useEffect, useState } from 'react';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';
import { OnboardingComponent } from 'src/interfaces/onboardingComponent.interface';
import { PhoneAgentCaller } from 'src/pages/Agents/components/PhoneAgentCaller';
import { AgentCaller } from 'src/pages/Agents/components/AgentCaller';
import { Button } from 'src/components/Button';
import { AnimatedCircles } from 'src/pages/Agents/components/AnimatedCircles';

type CallerProps = Omit<OnboardingComponent, 'secondaryAction'> & {
  secondaryAction: () => void;
};

export const Caller = ({
  onPhaseComplete,
  secondaryAction,
  organization,
}: CallerProps) => {
  const { get, patch } = useAuthedRequest();
  const notification = useNotification();

  const [loading, setLoading] = useState(true);
  const [currentAgent, setCurrentAgent] = useState<Agent | null>(null);
  const [fromNumber, setFromNumber] = useState('');
  const [didCreateCall, setDidCreateCall] = useState(false);
  const [mode, setMode] = useState<'phone' | 'web'>('phone');

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const numberQuery = new URLSearchParams({
          page: '1',
          size: '1',
          filters: JSON.stringify({ number: '' }),
        });

        const [agentsResponse, numbersResponse] = await Promise.all([
          get('/agents'),
          get(`/numbers?${numberQuery.toString()}`),
        ]);

        if (!active) return;

        const hasAgent = !!agentsResponse?.items?.length;
        const hasNumber = !!numbersResponse?.items?.length;

        if (!hasAgent || !hasNumber) {
          throw new Error(
            `Missing data: hasAgent=${hasAgent}, hasNumber=${hasNumber}`,
          );
        }

        setCurrentAgent(agentsResponse.items[0]);
        setFromNumber(numbersResponse.items[0].number);
      } catch (err) {
        if (!active) return;
        console.error('Failed to load onboarding data:', err);
        notification.error('Failed to load onboarding data...');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // INTENTIONALLY run only on mount to avoid re-fetch flicker
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = async () => {
    if (didCreateCall) {
      await patch(`/organizations/${organization.id}/onboarding`, {
        currentPhase: 'feedback',
      });
      onPhaseComplete();
    } else {
      await patch(`/organizations/${organization.id}/onboarding`, {
        currentPhase: 'ready',
        isCompleted: true,
      });
      secondaryAction();
    }
  };

  const loadedSuccessfully = !loading && !!currentAgent && !!fromNumber;

  return (
    <>
      <img
        src="/assets/logo-white.png"
        alt="Circle"
        className="absolute top-10 left-10 h-10 animate-fade-in-up"
      />

      <div className="container mx-auto py-10">
        <div className="flex flex-col items-center justify-center min-h-[80vh]">
          {loading ? (
            <div className="flex flex-col items-center">
              <AnimatedCircles
                isMaximized
                isCalling={false}
                animate
                variant="large"
              />
              <p className="text-2xl text-neutral-0 mt-6 mb-6 animate-fade-in-up">
                Loading Agent...
              </p>
            </div>
          ) : loadedSuccessfully ? (
            mode === 'phone' ? (
              <PhoneAgentCaller
                agent={currentAgent as Agent}
                fromNumber={fromNumber}
                onContinue={handleContinue}
                onCallCreated={() => {
                  setDidCreateCall(true);
                }}
                onWebCall={() => setMode('web')}
              />
            ) : (
              <AgentCaller
                agent={currentAgent as Agent}
                isEmbedded={true}
                onCallEnd={handleContinue}
                onCallStart={() => {
                  setDidCreateCall(true);
                }}
                customControls={
                  <div className="flex flex-col gap-2 w-full">
                    <Button
                      variant="outlined"
                      className="flex items-center gap-2 bg-neutral-0"
                      onClick={handleContinue}
                    >
                      Continue Onboarding
                    </Button>
                    <Button
                      variant="text"
                      className="text-neutral-0 underline px-0"
                      onClick={() => setMode('phone')}
                    >
                      Get a call on your phone instead
                    </Button>
                  </div>
                }
              />
            )
          ) : (
            <div className="flex flex-col items-center">
              <AnimatedCircles
                isMaximized
                isCalling={false}
                animate
                variant="large"
              />
              <p className="text-2xl text-neutral-0 mt-6 mb-6 animate-fade-in-up">
                An error occurred while loading the page. Please try again.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
